import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trackEvent } from '../utils/analytics';

const CartContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL;

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const updateCartData = (data) => {
    if (data.session_id) setSessionId(data.session_id);
    setCart(data);
  };

  const fetchCart = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/cart`, { credentials: 'include' });
      if (res.ok) updateCartData(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productSlug, quantity = 1, variantAlias = null, vin = null, selectedOptions = null) => {
    setLoading(true);
    try {
      const body = { product_slug: productSlug, quantity, variant_alias: variantAlias };
      if (vin) body.vin = vin;
      if (selectedOptions && selectedOptions.length > 0) body.selected_options = selectedOptions;
      const res = await fetch(`${API}/api/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        updateCartData(data);
        // Analytics
        const newItem = (data.items || []).find(i => i.slug === productSlug || i.product_slug === productSlug);
        trackEvent('add_to_cart', {
          slug: productSlug,
          name: newItem?.name || '',
          price: newItem?.price ?? null,
          qty: quantity,
          variant: variantAlias || null,
        });
        setNotification('Produkt zum Warenkorb hinzugefügt!');
        setTimeout(() => setNotification(null), 3000);
        // v154: signal for the global "Warenkorb speichern" popup (email capture)
        try { window.dispatchEvent(new CustomEvent('trxl:cart-add', { detail: { itemCount: data.item_count } })); } catch (e) { /* noop */ }
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const updateItem = async (itemKey, quantity) => {
    try {
      const res = await fetch(`${API}/api/cart/item/${encodeURIComponent(itemKey)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity }),
      });
      if (res.ok) updateCartData(await res.json());
    } catch (e) { /* silent */ }
  };

  const removeItem = async (itemKey) => {
    try {
      const res = await fetch(`${API}/api/cart/item/${encodeURIComponent(itemKey)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) updateCartData(await res.json());
    } catch (e) { /* silent */ }
  };

  const clearCart = async () => {
    try {
      const res = await fetch(`${API}/api/cart`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) updateCartData(await res.json());
    } catch (e) { /* silent */ }
  };

  const refreshCart = fetchCart;

  return (
    <CartContext.Provider value={{ cart, sessionId, loading, notification, addToCart, updateItem, removeItem, clearCart, refreshCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
